import CardSymphony from 'elements/CardSymphony';
import { calculateThemeColors, clsx, styx } from 'modules/Utils';
import { CSSProperties, useState } from 'react';
import WebsiteBuilderHeader from '../WebsiteBuilderHeader';
import ButtonSymphony from 'elements/ButtonSymphony';
import useWebsiteBuilder, { ImageContentStyle, WebsiteBuilder_imageExists } from 'Hooks/useWebsiteBuilder';
import CloudinaryUploadWidget from 'components/shareable/CloudinaryUploadWidget';
import Sty from 'modules/Sty';
import { getPalette } from 'react-palette';
import OptionsSymphony, { OptionItem } from 'elements/OptionsSymphony';
import ModalSymphony from 'elements/ModalSymphony';
import RecordSearchView from 'elements/RecordSearchView';
import { RecordDetails } from 'modules/Types';
import useScreen from 'Hooks/useScreen';
import Colors from 'modules/Colors';
import WebsiteBuilderFooter from '../WebsiteBuilderFooter';
import Icon from 'elements/Icon';
import SwitchSymphony from 'elements/SwitchSymphony';
import InputSymphony from 'elements/InputSymphony';
import useBrand from 'Hooks/useBrand';

interface ImageContentProps {
    className: string;
    contentIndex: number;
    tabSelectorComponent: React.ReactNode;
    id: string;
}

const ImageContent = ({
    className,
    contentIndex,
    tabSelectorComponent,
    id,
}: ImageContentProps) => {
    const {
        cssCalc,
        mStyle,
        mobileView,
    } = useScreen();
    const {
        isPaidBrand,
    } = useBrand();
    const {
        websiteContent,
        setWebsiteContent,
        websiteStyle,
        setWebsiteStyle,
    } = useWebsiteBuilder() as WebsiteBuilder_imageExists;

    const websiteContentId = websiteContent[contentIndex].id;
    const imageContent = websiteContent[contentIndex].imageContent;

    const imageContentStyle = imageContent.style;

    const requiresPaidPlan = imageContentStyle === ImageContentStyle.FULL_SCREEN || imageContentStyle === ImageContentStyle.CIRCLE || imageContentStyle === ImageContentStyle.ROUNDED;

    const uploadButtonId = `imageContentButton_${contentIndex}_${websiteContentId}`;

    const imageOptionStyle: CSSProperties = {
        width: 30,
        height: 30,
        objectFit: 'cover',
        backgroundColor: Colors.greyDark,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
    }

    const IMAGE_STYLE_OPTIONS: OptionItem[] = [
        {
            id: ImageContentStyle.SQUARE,
            label: 'Square',
            childrenLeft: (
                <div
                    style={styx(imageOptionStyle, {
                        backgroundImage: `url(${imageContent.image})`,
                    })}
                />
            ),
        },
        {
            id: ImageContentStyle.SMALL_CIRCLE,
            label: 'Small Circle',
            childrenLeft: (
                <div
                    style={styx(imageOptionStyle, {
                        borderRadius: 100,
                        backgroundImage: `url(${imageContent.image})`,
                    })}
                />
            ),
        },

        {
            id: ImageContentStyle.FULL_SCREEN,
            label: 'Full Screen',
            iconRight: 'gem',
            childrenLeft: (
                <Icon
                    className="imageContentFullScreenIcon"
                    style={{
                        marginLeft: -4,
                        marginRight: -8,
                    }}
                    icon="expand"
                    size={30}
                    color={imageContentStyle === ImageContentStyle.FULL_SCREEN ? Colors.purple : Colors.greyDark}
                />
            ),
        },
        {
            id: ImageContentStyle.CIRCLE,
            label: 'Circle',
            iconRight: 'gem',
            childrenLeft: (
                <div
                    style={styx(imageOptionStyle, {
                        borderRadius: 100,
                        backgroundImage: `url(${imageContent.image})`,
                    })}
                />
            ),
        },
        {
            id: ImageContentStyle.ROUNDED,
            label: 'Rounded',
            iconRight: 'gem',
            childrenLeft: (
                <div
                    style={styx(imageOptionStyle, {
                        borderRadius: 5,
                        backgroundImage: `url(${imageContent.image})`,
                    })}
                />
            ),
        },
    ];

    const [showReplaceImageModal, setShowReplaceImageModal] = useState<boolean>(false);
    const [showRecordSearchModal, setShowRecordSearchModal] = useState<boolean>(false);

    const updateImageContent = async (image: string) => {
        const updatedContent = [...websiteContent]
        updatedContent[contentIndex].imageContent.image = image;
        setWebsiteContent(updatedContent);

        if (contentIndex === 0) {
            const paletteData = await getPalette(image);
            const {
                backgroundColor,
                secondaryColor,
                textColor
            } = calculateThemeColors(paletteData);

            setWebsiteStyle({
                ...websiteStyle,
                backgroundColor,
                secondaryColor,
                textColor,
            });
        }
    }

    const handleSelectRecord = (record: RecordDetails) => {
        setShowRecordSearchModal(false);
        setShowReplaceImageModal(false);
        updateImageContent(record.thumbnail_url);
    }

    const handleOnUploadSuccess = async (file: { info: { secure_url: string } }) => {
        const image = file.info.secure_url;

        await updateImageContent(image);
        setShowReplaceImageModal(false);
    }

    const renderFileUploadButton = () => {
        return (
            <ButtonSymphony
                className="imageContentButton1"
                id={uploadButtonId}
                iconLeft="upload"
            >
                Upload Image
            </ButtonSymphony>
        )
    }

    const renderRightSideContent = () => {
        if (mobileView) {
            return (
                <Icon
                    className="imageContentButton2Icon"
                    icon="image"
                    size={24}
                    onClick={() => setShowReplaceImageModal(true)}
                />
            )
        }

        return (
            <>
                <ButtonSymphony
                    className="imageContentButton2"
                    onClick={() => setShowReplaceImageModal(true)}
                >
                    Choose Image
                </ButtonSymphony>
            </>
        )
    }

    // NOTE: render()
    return (
        <>
            <CardSymphony
                className={clsx("imageContentComponent", className)}
                style={styx(sty.imageContentComponent)}
                size="small"
                id={id}
            >
                <WebsiteBuilderHeader
                    contentIndex={contentIndex}
                    rightSideContent={
                        <>
                            {/* {renderRightSideContent()} */}
                            {tabSelectorComponent}
                        </>
                    }
                />
                <CloudinaryUploadWidget
                    uploadButtonId={uploadButtonId}
                    resourceType="image"
                    maxFiles={1}
                    showUploadMoreButton={false}
                    clientAllowedFormats={["jpg", "jpeg", "png"]}
                    minImageWidth={300}
                    onUploadAdded={(_fileId: string) => { }}
                    onUploadSuccess={handleOnUploadSuccess}
                    onUploadAbortAll={() => { }}
                    onUploadAbort={(_fileId: string) => { }}
                    onCancel={(_info: Record<string, unknown>) => { }}
                />
                <div style={Sty.text16grey}>
                    Select the artwork you want to display. We recommend using at least an image that's 1000x1000.
                </div>
                {imageContent.image &&
                    <div
                        className="imageContentArtworkPreviewContainer"
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 16,
                            border: Sty.border,
                            borderRadius: 16,
                            padding: 16,
                        }}
                    >
                        <img
                            className="imageContentArtworkPreview"
                            style={{
                                width: 75,
                                height: 75,
                                objectFit: 'cover',
                                borderRadius: 8,
                                overflow: 'hidden',
                            }}
                            alt="artwork"
                            src={imageContent.image}
                        />
                        <div style={Sty.text16greyBold}>
                            My Image
                        </div>
                        <ButtonSymphony
                            className="imageContentArtworkPreviewButton"
                            style={{ marginLeft: 'auto' }}
                            onClick={() => setShowReplaceImageModal(true)}
                            variant="text"
                            color="purple"
                        >
                            Replace
                        </ButtonSymphony>
                    </div>
                }
                <SwitchSymphony
                    className="textContentTitleSwitch"
                    style={{ marginTop: 24 }}
                    checked={imageContent.isTitle}
                    onChange={(value) => {
                        const updatedContent = [...websiteContent];
                        updatedContent[contentIndex].imageContent.isTitle = value;
                        if (!value) {
                            updatedContent[contentIndex].imageContent.isSubtitle = false;
                        }
                        setWebsiteContent(updatedContent);
                    }}
                    label="Add Title Text"
                />
                {imageContent.isTitle &&
                    <>
                        <InputSymphony
                            className="textContentTitleInput"
                            value={imageContent.title || ''}
                            onChange={(value) => {
                                const updatedContent = [...websiteContent];
                                updatedContent[contentIndex].imageContent.title = value || '';
                                setWebsiteContent(updatedContent);
                            }}
                            placeholder="Title"
                            label="Title"
                        />
                        <SwitchSymphony
                            className="textContentSubtitleSwitch"
                            checked={imageContent.isSubtitle}
                            onChange={(value) => {
                                const updatedContent = [...websiteContent];
                                updatedContent[contentIndex].imageContent.isSubtitle = value;
                                setWebsiteContent(updatedContent);
                            }}
                            label="Add Subtitle Text"
                        />
                        {imageContent.isSubtitle &&
                            <InputSymphony
                                className="imageContentSubtitleInput"
                                value={imageContent.subtitle || ''}
                                onChange={(value) => {
                                    const updatedContent = [...websiteContent];
                                    updatedContent[contentIndex].imageContent.subtitle = value || '';
                                    setWebsiteContent(updatedContent);
                                }}
                                placeholder="Subtitle"
                                label="Subtitle"
                            />
                        }
                    </>
                }

                {/* STYLE OPTIONS */}
                <WebsiteBuilderFooter
                    className="imageContentStyleContainer"
                    contentIndex={contentIndex}
                    requiresPaidPlan={requiresPaidPlan}
                    isBackgroundColor
                    isTextColor
                    titleRequired="This image style requires a paid plan to publish your website."
                    styleOptions={
                        <>
                            <OptionsSymphony
                                className="imageStyleOptions"
                                style={{ flexWrap: 'wrap' }}
                                options={IMAGE_STYLE_OPTIONS}
                                value={imageContentStyle || ''}
                                handleOptionSelected={(option) => {
                                    const updatedContent = [...websiteContent];

                                    updatedContent[contentIndex].imageContent.style = option as ImageContentStyle;
                                    setWebsiteContent(updatedContent);
                                }}
                                size="small"
                                isRadio={false}
                            />
                        </>
                    }
                />
            </CardSymphony>

            {/* MODALS */}
            <ModalSymphony
                className="imageContentModal"
                isOpen={showReplaceImageModal}
                onClose={() => setShowReplaceImageModal(false)}
                title="Replace Image"
                subtitle="Choose an image to replace the current image."
                style={{ gap: 16 }}
            >
                <div
                    className="imageContentModalButtonsContainer"
                    style={mStyle(sty, 'imageContentModalButtonsContainer')}
                >
                    {renderFileUploadButton()}
                    <ButtonSymphony
                        className="imageContentModalButton"
                        onClick={() => setShowRecordSearchModal(true)}
                        iconLeft="music"
                    >
                        Pick from release
                    </ButtonSymphony>
                </div>
            </ModalSymphony>

            <ModalSymphony
                className="recordImageSearchModal"
                isOpen={showRecordSearchModal}
                onClose={() => setShowRecordSearchModal(false)}
                title="Pick from release"
                subtitle="Choose an image to replace the current image."
            >
                <RecordSearchView
                    className="recordImageSearchViewImageContent"
                    handleSelectRecord={handleSelectRecord}
                    options={['single', 'project', 'playlist']}
                    letUserSearchSpotify
                    letUserRefreshReleases
                    maxHeight={cssCalc(64 + 280)}
                />
            </ModalSymphony>
        </>
    )
}

const sty: Record<string, CSSProperties> = {
    imageContentComponent: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    imageContentModalButtonsContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: 16,
        justifyContent: 'center',
        alignItems: 'center',
    },
    imageContentModalButtonsContainer_mobile: {
        flexDirection: 'column',
    }
}

export default ImageContent;